import { useState, useEffect } from "react";
import { FREE_DELIVERY_THRESHOLD } from "@/components/FreeDeliveryBar";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { api, formatGBP, getDeviceId, customerApi } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { toast } from "sonner";
import { Lock, Tag, X, Gift, Truck, Store, Check, Scissors, ChevronDown, HelpCircle } from "lucide-react";
import { CustomFitSummary } from "@/components/CustomiseFit";

const CONTACT_FIELDS = [
  { name: "full_name", label: "Full Name", type: "text", required: true, half: true },
  { name: "email", label: "Email Address", type: "email", required: true, half: true },
  { name: "phone", label: "Phone Number", type: "tel", required: true, half: true },
];

const ADDRESS_FIELDS = [
  { name: "postcode", label: "Postcode", type: "text", required: true, half: true },
  { name: "address", label: "Address", type: "text", required: true, half: false },
  { name: "city", label: "City / Town", type: "text", required: true, half: true },
  { name: "country", label: "Country", type: "text", required: true, half: true },
];

const FIELDS = [...CONTACT_FIELDS, ...ADDRESS_FIELDS];

const BILLING_FIELDS = [
  { name: "full_name", label: "Full Name", type: "text", half: true },
  { name: "postcode", label: "Postcode", type: "text", half: true },
  { name: "address", label: "Address", type: "text", half: false },
  { name: "city", label: "City / Town", type: "text", half: true },
  { name: "country", label: "Country", type: "text", half: true },
];

const UK_POSTCODE_RE = /^([Gg][Ii][Rr] ?0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9]?[A-Za-z])))) ?[0-9][A-Za-z]{2})$/;

const isUKCountry = (c) => {
  const v = String(c || "").trim().toLowerCase();
  return ["united kingdom", "uk", "u.k.", "great britain", "gb", "england", "scotland", "wales", "northern ireland"].includes(v);
};

export default function Checkout() {
  const { cart, cartId, refresh } = useCart();
  const { customer } = useCustomerAuth();
  const { format, currency, isConverted } = useCurrency();
  const navigate = useNavigate();
  const saved = (() => {
    try { return JSON.parse(localStorage.getItem("zebaa_checkout_form") || "null") || {}; }
    catch { return {}; }
  })();
  const [form, setForm] = useState(saved.form || { country: "United Kingdom" });
  const [fulfillment, setFulfillment] = useState(saved.fulfillment || "delivery");
  const [notes, setNotes] = useState(saved.notes || "");
  const [newsConsent, setNewsConsent] = useState(saved.newsConsent ?? true);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [billingSame, setBillingSame] = useState(saved.billingSame ?? true);
  const [billing, setBilling] = useState(saved.billing || { country: "United Kingdom" });
  const [loading, setLoading] = useState(false);
  const [codeInput, setCodeInput] = useState("");
  const [discount, setDiscount] = useState(null);
  const [codeNeedsAccount, setCodeNeedsAccount] = useState(false);
  const [useRewards, setUseRewards] = useState(false);
  const [applying, setApplying] = useState(false);
  const [reserveSecs, setReserveSecs] = useState(null);
  const [reserving, setReserving] = useState(false);

  // Persist the customer's details so leaving & returning to checkout keeps them.
  useEffect(() => {
    localStorage.setItem("zebaa_checkout_form", JSON.stringify({ form, notes, newsConsent, billingSame, billing, fulfillment }));
  }, [form, notes, newsConsent, billingSame, billing, fulfillment]);

  const reserveItems = async () => {
    if (!cartId || !cart.items || cart.items.length === 0) return;
    const items = cart.items.map((it) => ({
      product_id: it.product_id,
      colour: it.colour || null,
      size: it.size || null,
      quantity: it.quantity || 1,
    }));
    setReserving(true);
    try {
      const { data } = await api.post("/reservations", { cart_id: cartId, items });
      setReserveSecs(data.ttl_seconds);
    } catch (e) {
      if (e?.response?.status === 409) {
        const d = e.response.data?.detail;
        toast.error(typeof d === "string" ? d : (d?.message || "Some items are no longer available."));
      }
    } finally {
      setReserving(false);
    }
  };

  // On open, continue an existing reservation (don't reset its timer). Only create
  // a fresh 5-minute hold if there isn't an active one for this cart.
  useEffect(() => {
    let active = true;
    (async () => {
      if (!cartId || !cart.items || cart.items.length === 0) return;
      try {
        const { data } = await api.get(`/reservations/${cartId}`);
        if (active && data.active && data.seconds_remaining > 0) {
          setReserveSecs(data.seconds_remaining);
          return;
        }
      } catch { /* fall through to create a new reservation */ }
      if (active) reserveItems();
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartId, cart.items?.length]);

  // Tick the reservation countdown once per second.
  useEffect(() => {
    if (reserveSecs === null) return undefined;
    const t = setInterval(() => setReserveSecs((s) => (s === null ? null : Math.max(0, s - 1))), 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reserveSecs === null]);

  useEffect(() => {
    if (!customer) return;
    setForm((f) => ({ ...f, email: f.email || customer.email, full_name: f.full_name || customer.name }));
    customerApi().get("/account/addresses").then(({ data }) => {
      const def = data.find((a) => a.is_default) || data[0];
      if (def) {
        setForm((f) => ({
          ...f,
          full_name: f.full_name || def.full_name,
          phone: f.phone || def.phone,
          address: f.address || def.address,
          city: f.city || def.city,
          postcode: f.postcode || def.postcode,
          country: def.country || f.country,
        }));
      }
    }).catch(() => {});
  }, [customer]);

  const discountAmount = discount ? discount.discount_amount : 0;
  const customFeeTotal = (cart.items || []).reduce((s, it) => s + (it.custom_fee || 0), 0);
  const itemsSubtotal = Math.max(0, cart.subtotal - customFeeTotal);
  const discountedSubtotal = Math.max(0, cart.subtotal - discountAmount);
  const rewardsBalance = (customer && customer.rewards_balance) || 0;
  const rewardsApplied = useRewards ? Math.min(rewardsBalance, discountedSubtotal) : 0;
  const isCollect = fulfillment === "collect";
  const shipping = isCollect || discountedSubtotal >= FREE_DELIVERY_THRESHOLD || cart.subtotal === 0 ? 0 : 4.99;
  const total = Math.max(0, discountedSubtotal - rewardsApplied) + shipping;
  const rewardsToEarn = customer ? Math.floor(Math.max(0, discountedSubtotal - rewardsApplied) / 25) : 0;

  const removeCode = () => {
    setDiscount(null);
    setCodeInput("");
    setCodeNeedsAccount(false);
  };

  const applyCode = async () => {
    if (!codeInput.trim()) return;
    if (discount) {
      toast.error("Only one discount code can be applied per order. Remove the current code first.");
      return;
    }
    setApplying(true);
    try {
      const { data } = await api.post("/discount/validate", {
        code: codeInput.trim(),
        subtotal: cart.subtotal,
        cart_id: cartId,
        device_id: getDeviceId(),
        email: (customer && customer.email) || form.email || null,
      });
      if (data.valid) {
        if (data.requires_account && !customer) {
          setDiscount(null);
          setCodeNeedsAccount(true);
          toast.error("Sign in or create an account to use this offer.");
          return;
        }
        setCodeNeedsAccount(false);
        setDiscount(data);
        toast.success(`Code applied - you saved ${format(data.discount_amount)}`);
        if (data.note) toast.info(data.note);
      } else {
        setDiscount(null);
        setCodeNeedsAccount(false);
        toast.error(data.message || "Invalid code");
      }
    } catch {
      toast.error("Could not validate code");
    } finally {
      setApplying(false);
    }
  };

  const update = (name, value) => setForm((f) => ({ ...f, [name]: value }));
  const updateBilling = (name, value) => setBilling((b) => ({ ...b, [name]: value }));

  const submit = async (e) => {
    e.preventDefault();
    if (cart.items.length === 0) {
      toast.error("Your bag is empty");
      return;
    }
    if (!billingSame) {
      const missing = BILLING_FIELDS.find((f) => !String(billing[f.name] || "").trim());
      if (missing) {
        toast.error("Please complete your billing address");
        return;
      }
    }
    if (isCollect) {
      // Click & Collect: only contact details required.
      const missing = CONTACT_FIELDS.find((f) => !String(form[f.name] || "").trim());
      if (missing) {
        toast.error("Please enter your name, email and phone number for collection.");
        return;
      }
    } else {
      // UK-only shipping
      if (!isUKCountry(form.country)) {
        toast.error("Sorry, we currently only ship within the United Kingdom.");
        return;
      }
      if (!UK_POSTCODE_RE.test(String(form.postcode || "").trim())) {
        toast.error("Please enter a valid UK postcode.");
        return;
      }
      if (!billingSame) {
        if (!isUKCountry(billing.country)) {
          toast.error("Billing address must be in the United Kingdom.");
          return;
        }
        if (!UK_POSTCODE_RE.test(String(billing.postcode || "").trim())) {
          toast.error("Please enter a valid UK billing postcode.");
          return;
        }
      }
    }
    setLoading(true);
    if (newsConsent && form.email) {
      api.post("/newsletter", { email: form.email, source: "checkout" }).catch(() => {});
    }
    try {
      const client = customer ? customerApi() : api;
      const { data } = await client.post("/orders", {
        cart_id: cartId,
        shipping: { ...form, notes },
        billing_same_as_shipping: isCollect ? true : billingSame,
        billing: isCollect || billingSame ? null : billing,
        discount_code: discount ? discount.code : null,
        device_id: getDeviceId(),
        rewards_applied: rewardsApplied,
        fulfillment,
      });
      localStorage.removeItem("zebaa_welcome_code");
      localStorage.removeItem("zebaa_checkout_form");
      // Create a Stripe Checkout session for this order and redirect to secure payment.
      const { data: pay } = await client.post("/payments/checkout", {
        order_number: data.order_number,
        origin_url: window.location.origin,
      });
      if (pay.checkout_url) {
        window.location.href = pay.checkout_url;
        return;
      }
      // Fully covered by rewards/discount — no payment needed.
      await refresh();
      navigate(`/order-confirmation/${data.order_number}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
      setLoading(false);
    }
  };

  if (cart.items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-28 text-center" data-testid="checkout-empty">
        <h1 className="font-serif text-4xl text-charcoal">Your Bag is Empty</h1>
        <p className="text-gray-500 mt-3">Add some items before checking out.</p>
        <Link to="/shop/all" className="inline-block mt-8 bg-pink text-white px-9 py-4 text-xs uppercase tracking-[0.18em] hover:bg-pink-hover transition-colors">Shop Now</Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="checkout-page">
      <h1 className="font-serif text-5xl text-charcoal mb-2">Checkout</h1>
      <p className="text-gray-500 mb-6">Complete your details to place your order.</p>
      {reserveSecs !== null && (
        <div
          data-testid="reservation-banner"
          className={`mb-6 flex items-center gap-2.5 rounded-lg px-4 py-3 text-sm ${reserveSecs > 0 ? "bg-pink/5 border border-pink/20 text-charcoal" : "bg-red-50 border border-red-200 text-red-700"}`}
        >
          <Lock className={`w-4 h-4 shrink-0 ${reserveSecs > 0 ? "text-pink" : "text-red-600"}`} />
          {reserveSecs > 0 ? (
            <span>
              Your items are{" "}
              <span className="font-semibold" data-testid="reservation-countdown">
                Reserved for {Math.floor(reserveSecs / 60)}:{String(reserveSecs % 60).padStart(2, "0")}
              </span>
            </span>
          ) : (
            <span className="flex flex-wrap items-center gap-x-2 gap-y-1" data-testid="reservation-expired">
              <span>Your reservation has expired — your items may no longer be held for you.</span>
              <button
                type="button"
                onClick={reserveItems}
                disabled={reserving}
                data-testid="reservation-refresh"
                className="underline font-semibold hover:text-red-800 disabled:opacity-60"
              >
                {reserving ? "Refreshing…" : "Refresh reservation"}
              </button>
            </span>
          )}
        </div>
      )}
      {!customer && (
        <div className="mb-8 rounded-lg p-5 text-white flex flex-col sm:flex-row sm:items-center justify-between gap-4" style={{ background: "linear-gradient(135deg, #2a1622 0%, #4a2036 55%, #6d2a4c 100%)" }} data-testid="checkout-guest-banner">
          <div className="flex items-start gap-3">
            <Gift className="w-6 h-6 text-gold-bright shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium">Create an account to claim rewards on this order</p>
              <p className="text-xs text-white/60 mt-0.5">Earn £1 for every £25 you spend, redeemable at checkout — plus order tracking & faster checkout.</p>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <Link to={`/register?redirect=/checkout${form.email ? `&email=${encodeURIComponent(form.email)}` : ""}`}
              data-testid="checkout-create-account"
              className="bg-white/95 text-charcoal px-5 py-2.5 rounded-full text-xs uppercase tracking-[0.14em] hover:bg-white transition-colors whitespace-nowrap">
              Create Account
            </Link>
            <Link to="/login?redirect=/checkout" className="text-xs uppercase tracking-[0.12em] text-white/70 hover:text-white transition-colors whitespace-nowrap" data-testid="checkout-guest-signin">
              Sign In
            </Link>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          {/* Mobile collapsible order summary (reference style) */}
          <div className="lg:hidden mb-8 bg-surface rounded-2xl border border-gray-200 overflow-hidden" data-testid="checkout-mobile-summary">
            <button type="button" onClick={() => setSummaryOpen((o) => !o)} data-testid="checkout-mobile-summary-toggle"
              className="w-full flex items-center justify-between px-5 py-4">
              <span className="flex items-center gap-2 text-sm text-charcoal">
                <Tag className="w-4 h-4 text-pink" />
                {summaryOpen ? "Hide order summary" : "Show order summary"}
                <ChevronDown className={`w-4 h-4 transition-transform ${summaryOpen ? "rotate-180" : ""}`} />
              </span>
              <span className="font-medium text-charcoal" data-testid="checkout-mobile-total">{format(total)}</span>
            </button>
            {summaryOpen && (
              <div className="px-5 pb-5 border-t border-gray-200 pt-4 animate-fade-in">
                <div className="space-y-4">
                  {cart.items.map((item) => (
                    <div key={item.line_id || item.product_id} className="flex gap-3 items-center">
                      <div className="relative shrink-0">
                        <img src={item.image} alt={item.name} className="w-14 h-16 object-cover bg-white rounded-md border border-gray-200" />
                        <span className="absolute -top-2 -right-2 bg-charcoal text-white text-[11px] w-5 h-5 rounded-full flex items-center justify-center">{item.quantity}</span>
                      </div>
                      <div className="flex-1 text-sm min-w-0">
                        <p className="font-serif truncate">{item.name}</p>
                        {(item.colour || item.size) && (
                          <p className="text-xs text-gray-500 mt-0.5">{[item.colour, item.size].filter(Boolean).join(" · ")}</p>
                        )}
                        <CustomFitSummary customization={item.customization} compact />
                      </div>
                      <span className="text-sm whitespace-nowrap">{format(item.line_total)}</span>
                    </div>
                  ))}
                </div>
                <div className="w-full h-px bg-gray-200 my-4" />
                {discount ? (
                  <div className="flex items-center justify-between gap-2 mb-3 bg-pink/5 border border-pink/20 rounded-md px-3 py-2.5">
                    <span className="flex items-center gap-2 text-sm text-charcoal">
                      <Tag className="w-4 h-4 text-pink" />
                      <span><span className="font-semibold uppercase tracking-wide">{discount.code}</span> · saved {format(discountAmount)}</span>
                    </span>
                    <button type="button" onClick={removeCode} className="text-gray-400 hover:text-pink" aria-label="Remove discount code"><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <div className="flex items-stretch gap-2 mb-3">
                    <div className="flex-1 flex items-center bg-white border border-gray-200 rounded-xl px-3 shadow-sm">
                      <Gift className="w-4 h-4 text-gray-400 mr-2" />
                      <input value={codeInput} onChange={(e) => setCodeInput(e.target.value)} placeholder="Gift card or discount code"
                        data-testid="checkout-mobile-discount-input" className="w-full py-2.5 text-sm uppercase tracking-wide bg-transparent focus:outline-none" />
                    </div>
                    <button type="button" onClick={applyCode} disabled={applying} data-testid="checkout-mobile-apply-discount"
                      className="bg-charcoal text-white px-5 rounded-xl text-xs uppercase tracking-[0.12em] hover:bg-pink transition-colors disabled:opacity-60">
                      {applying ? "..." : "Apply"}
                    </button>
                  </div>
                )}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>{format(itemsSubtotal)}</span></div>
                  {customFeeTotal > 0 && (
                    <div className="flex justify-between"><span className="text-gray-500 flex items-center gap-1.5"><Scissors className="w-3.5 h-3.5 text-gold" /> Custom tailoring</span><span>+{format(customFeeTotal)}</span></div>
                  )}
                  {discount && (
                    <div className="flex justify-between text-pink"><span>Discount</span><span>−{format(discountAmount)}</span></div>
                  )}
                  <div className="flex justify-between"><span className="text-gray-500">Shipping</span><span>{shipping === 0 ? "Free" : format(shipping)}</span></div>
                  {rewardsApplied > 0 && (
                    <div className="flex justify-between text-metallic-gold"><span>Rewards applied</span><span>−{format(rewardsApplied)}</span></div>
                  )}
                  <div className="w-full h-px bg-gray-200 my-1" />
                  <div className="flex justify-between text-base font-medium"><span>Total</span><span>{format(total)}</span></div>
                  {isConverted && (
                    <p className="text-xs text-gray-400">Prices shown in {currency}. Charged in GBP ({formatGBP(total)}).</p>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="mb-8">
            <h2 className="font-serif text-2xl mb-4">How would you like to receive your order?</h2>
            <div className="grid sm:grid-cols-2 gap-3" data-testid="checkout-fulfillment-options">
              <button
                type="button"
                onClick={() => setFulfillment("delivery")}
                data-testid="fulfillment-delivery"
                className={`flex items-center gap-3 text-left rounded-2xl p-4 border-2 transition-colors ${!isCollect ? "border-pink bg-pink/[0.04]" : "border-gray-200 hover:border-gray-300"}`}
              >
                <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${!isCollect ? "border-pink" : "border-gray-300"}`}>
                  {!isCollect && <span className="w-2.5 h-2.5 rounded-full bg-pink" />}
                </span>
                <Truck className="w-5 h-5 text-gold shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-charcoal">Home Delivery</p>
                  <p className="text-xs text-gray-500 mt-0.5">Royal Mail Tracked · UK only</p>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setFulfillment("collect")}
                data-testid="fulfillment-collect"
                className={`flex items-center gap-3 text-left rounded-2xl p-4 border-2 transition-colors ${isCollect ? "border-pink bg-pink/[0.04]" : "border-gray-200 hover:border-gray-300"}`}
              >
                <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${isCollect ? "border-pink" : "border-gray-300"}`}>
                  {isCollect && <span className="w-2.5 h-2.5 rounded-full bg-pink" />}
                </span>
                <Store className="w-5 h-5 text-gold shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-charcoal">Click &amp; Collect</p>
                  <p className="text-xs text-gray-500 mt-0.5">Free · Green Street store</p>
                </div>
              </button>
            </div>
          </div>

          <h2 className="font-serif text-2xl mb-6">{isCollect ? "Your Details" : "Shipping Details"}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {(isCollect ? CONTACT_FIELDS : FIELDS).map((f) => (
              <div key={f.name} className={f.half ? "" : "sm:col-span-2"}>
                <label className="text-xs uppercase tracking-[0.12em] text-gray-500">{f.label}</label>
                {f.name === "country" ? (
                  <input
                    type="text"
                    value="United Kingdom"
                    readOnly
                    aria-readonly="true"
                    data-testid="checkout-country"
                    title="We currently ship within the United Kingdom only"
                    className="mt-1.5 w-full bg-surface border border-gray-200 rounded-xl px-4 py-3.5 text-sm text-gray-500 shadow-sm cursor-not-allowed"
                  />
                ) : (
                  <input
                    type={f.type}
                    required={f.required}
                    value={form[f.name] || ""}
                    onChange={(e) => update(f.name, e.target.value)}
                    data-testid={`checkout-${f.name}`}
                    placeholder={f.name === "postcode" ? "e.g. E13 9AP" : undefined}
                    className="mt-1.5 w-full bg-white border border-gray-200 rounded-xl px-4 py-3.5 text-sm shadow-sm focus:outline-none focus:border-pink focus:ring-2 focus:ring-pink/15 hover:border-gray-300 transition-all"
                  />
                )}
              </div>
            ))}
            {isCollect && (
              <div className="sm:col-span-2 flex items-start gap-3 bg-surface border border-gray-200 rounded-xl px-4 py-3.5" data-testid="checkout-pickup-info">
                <Store className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-charcoal">Collect from our store</p>
                  <p className="text-gray-600 mt-0.5">Unit 1, 312 Green Street, London, E13 9AP</p>
                  <p className="text-gray-500 text-xs mt-1">Ready to collect from <span className="font-medium text-charcoal">11am the next day</span> — no need to book a slot, just come any time during opening hours (Mon–Sat 10am–7pm · Sun 11am–5pm). We'll email you when it's ready.</p>
                </div>
              </div>
            )}
            <div className="sm:col-span-2">
              <label className="flex items-start gap-3 bg-surface border border-gray-200 rounded-xl px-4 py-3.5 cursor-pointer select-none" data-testid="checkout-news-consent-label">
                <input
                  type="checkbox"
                  checked={newsConsent}
                  onChange={(e) => setNewsConsent(e.target.checked)}
                  data-testid="checkout-news-consent"
                  className="w-4 h-4 accent-pink mt-0.5"
                />
                <span className="text-sm text-charcoal">
                  Email me with news and offers
                  <span className="block text-xs text-gray-500 mt-0.5">We'll use your email for order updates. Tick to also receive new arrivals, sales and exclusive offers. Unsubscribe anytime.</span>
                </span>
              </label>
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs uppercase tracking-[0.12em] text-gray-500">Order Notes (optional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                data-testid="checkout-notes"
                className="mt-1.5 w-full bg-white border border-gray-200 rounded-xl px-4 py-3.5 text-sm shadow-sm focus:outline-none focus:border-pink focus:ring-2 focus:ring-pink/15 hover:border-gray-300 transition-all resize-none"
              />
            </div>
          </div>

          <div className="mt-8" style={{ display: isCollect ? "none" : undefined }}>
            <h2 className="font-serif text-2xl mb-4">Billing Address</h2>
            <label
              className="flex items-center gap-3 bg-surface border border-gray-200 rounded-xl px-4 py-3.5 cursor-pointer select-none"
              data-testid="checkout-billing-same-toggle"
            >
              <input
                type="checkbox"
                checked={billingSame}
                onChange={(e) => setBillingSame(e.target.checked)}
                data-testid="checkout-billing-same-checkbox"
                className="w-4 h-4 accent-pink"
              />
              <span className="text-sm text-charcoal">Billing address is the same as my delivery address</span>
            </label>

            {!billingSame && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4" data-testid="checkout-billing-fields">
                {BILLING_FIELDS.map((f) => (
                  <div key={f.name} className={f.half ? "" : "sm:col-span-2"}>
                    <label className="text-xs uppercase tracking-[0.12em] text-gray-500">{f.label}</label>
                    {f.name === "country" ? (
                      <input
                        type="text"
                        value="United Kingdom"
                        readOnly
                        aria-readonly="true"
                        data-testid="checkout-billing-country"
                        className="mt-1.5 w-full bg-surface border border-gray-200 rounded-xl px-4 py-3.5 text-sm text-gray-500 shadow-sm cursor-not-allowed"
                      />
                    ) : (
                      <input
                        type={f.type}
                        value={billing[f.name] || ""}
                        onChange={(e) => updateBilling(f.name, e.target.value)}
                        data-testid={`checkout-billing-${f.name}`}
                        placeholder={f.name === "postcode" ? "e.g. E13 9AP" : undefined}
                        className="mt-1.5 w-full bg-white border border-gray-200 rounded-xl px-4 py-3.5 text-sm shadow-sm focus:outline-none focus:border-pink focus:ring-2 focus:ring-pink/15 hover:border-gray-300 transition-all"
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-10">
            <h2 className="font-serif text-2xl mb-4">{isCollect ? "Collection" : "Delivery Method"}</h2>
            <div
              data-testid="checkout-delivery-method"
              className="flex items-center gap-4 border-2 border-pink rounded-2xl bg-pink/[0.04] p-4 sm:p-5"
            >
              <span className="w-5 h-5 rounded-full border-2 border-pink flex items-center justify-center shrink-0" aria-hidden="true">
                <span className="w-2.5 h-2.5 rounded-full bg-pink" />
              </span>
              <span className="w-11 h-11 rounded-full bg-white shadow-sm flex items-center justify-center shrink-0">
                {isCollect ? <Store className="w-5 h-5 text-gold" /> : <Truck className="w-5 h-5 text-gold" />}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-charcoal">{isCollect ? "Click & Collect" : "Standard Delivery"}</p>
                <p className="text-xs text-gray-500 mt-0.5">{isCollect ? "Unit 1, 312 Green Street, London E13 9AP" : "Royal Mail Tracked 48 · 2–3 working days"}</p>
              </div>
              <span className="text-sm font-semibold text-charcoal whitespace-nowrap" data-testid="checkout-delivery-price">
                {shipping === 0 ? "Free" : format(shipping)}
              </span>
            </div>
            {!isCollect && shipping !== 0 && (
              <p className="text-xs text-gray-400 mt-2 flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                Add {format(FREE_DELIVERY_THRESHOLD - discountedSubtotal)} more to unlock free delivery.
              </p>
            )}
          </div>

          <div className="mt-8 bg-surface rounded-2xl p-5 flex items-start gap-3">
            <Lock className="w-5 h-5 text-gold shrink-0 mt-0.5" />
            <p className="text-sm text-gray-600">
              You'll be redirected to our <strong>secure Stripe payment page</strong> to complete your order.
              We never see or store your card details.
            </p>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="place-order-btn-mobile"
            className="lg:hidden mt-6 flex items-center justify-center gap-2 w-full text-white py-4 rounded-full text-sm font-medium shadow-sm bg-[#1d4ed8] hover:bg-[#1e40af] transition-all disabled:opacity-60"
          >
            {loading ? "Redirecting to payment..." : `Proceed to payment · ${format(total)}`}
          </button>
        </div>

        <div className="hidden lg:block bg-surface rounded-2xl p-8 h-fit sticky top-28" data-testid="checkout-summary">
          <h2 className="font-serif text-2xl mb-6">Your Order</h2>
          <div className="space-y-4 max-h-64 overflow-y-auto pr-1">
            {cart.items.map((item) => (
              <div key={item.line_id || item.product_id} className="flex gap-3">
                <img src={item.image} alt={item.name} className="w-14 h-16 object-cover bg-white" />
                <div className="flex-1 text-sm">
                  <p className="font-serif">{item.name}</p>
                  {(item.colour || item.size) && (
                    <p className="text-xs text-gray-500 mt-0.5">{[item.colour, item.size].filter(Boolean).join(" · ")}</p>
                  )}
                  <p className="text-xs text-gray-500">Qty {item.quantity}</p>
                  <CustomFitSummary customization={item.customization} compact />
                </div>
                <span className="text-sm">{format(item.line_total)}</span>
              </div>
            ))}
          </div>
          <div className="w-full h-px bg-gray-200 my-5" />
          {discount ? (
            <div className="flex items-center justify-between gap-2 mb-4 bg-pink/5 border border-pink/20 rounded-md px-3 py-2.5" data-testid="checkout-applied-code">
              <span className="flex items-center gap-2 text-sm text-charcoal">
                <Tag className="w-4 h-4 text-pink" />
                <span><span className="font-semibold uppercase tracking-wide">{discount.code}</span> applied — you saved {format(discountAmount)}</span>
              </span>
              <button type="button" onClick={removeCode} data-testid="checkout-remove-discount"
                className="text-gray-400 hover:text-pink transition-colors" aria-label="Remove discount code">
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-stretch gap-2 mb-1">
              <div className="flex-1 flex items-center bg-white border border-gray-200 rounded-xl px-3 shadow-sm focus-within:border-pink focus-within:ring-2 focus-within:ring-pink/15 transition-all">
                <Tag className="w-4 h-4 text-gray-400 mr-2" />
                <input
                  value={codeInput}
                  onChange={(e) => setCodeInput(e.target.value)}
                  placeholder="Discount code"
                  data-testid="checkout-discount-input"
                  className="w-full py-2.5 text-sm uppercase tracking-wide bg-transparent focus:outline-none"
                />
              </div>
              <button
                type="button"
                onClick={applyCode}
                disabled={applying}
                data-testid="checkout-apply-discount"
                className="bg-charcoal text-white px-5 rounded-xl text-xs uppercase tracking-[0.12em] hover:bg-pink transition-colors disabled:opacity-60"
              >
                {applying ? "..." : "Apply"}
              </button>
            </div>
          )}
          {!discount && (
            <p className="text-xs text-gray-400 mb-4">One discount code per order.</p>
          )}
          {codeNeedsAccount && !customer && (
            <div className="mb-4 -mt-1 bg-pink/5 border border-pink/20 rounded-md px-3 py-2.5 text-xs text-charcoal" data-testid="checkout-offer-needs-account">
              This offer is for account holders only.{" "}
              <Link to="/login?redirect=/checkout" className="text-pink font-semibold hover:underline">Sign in</Link>
              {" "}or{" "}
              <Link to="/register?redirect=/checkout" className="text-pink font-semibold hover:underline">create a free account</Link>
              {" "}to use it.
            </div>
          )}
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>{format(itemsSubtotal)}</span></div>
            {customFeeTotal > 0 && (
              <div className="flex justify-between" data-testid="checkout-tailoring-line">
                <span className="text-gray-500 flex items-center gap-1.5"><Scissors className="w-3.5 h-3.5 text-gold" /> Custom tailoring</span>
                <span>+{format(customFeeTotal)}</span>
              </div>
            )}
            {discount && (
              <div className="flex justify-between text-pink" data-testid="checkout-discount-line">
                <span>Discount ({discount.code})</span><span>−{format(discountAmount)}</span>
              </div>
            )}
            {discount && discount.note && (
              <p className="text-xs text-gray-400 -mt-1" data-testid="checkout-discount-note">{discount.note}</p>
            )}
            <div className="flex justify-between"><span className="text-gray-500">Shipping</span><span>{shipping === 0 ? "Free" : format(shipping)}</span></div>
            <p className="text-xs text-gray-400 -mt-1">{isCollect ? "Click & Collect · Unit 1, 312 Green Street, London E13 9AP" : "Royal Mail Tracked 48 · 2–3 working days"}</p>
            {customer && rewardsBalance > 0 && (
              <label className="flex items-start gap-2.5 bg-surface border border-gray-200 rounded-md px-3 py-2.5 cursor-pointer" data-testid="checkout-rewards-toggle">
                <input type="checkbox" checked={useRewards} onChange={(e) => setUseRewards(e.target.checked)} className="mt-0.5" data-testid="checkout-use-rewards" />
                <span className="text-xs text-charcoal">
                  Apply my rewards balance <span className="font-semibold text-metallic-gold">({format(rewardsBalance)})</span>
                  <span className="block text-gray-400">You'll save {format(Math.min(rewardsBalance, discountedSubtotal))} on this order.</span>
                </span>
              </label>
            )}
            {rewardsApplied > 0 && (
              <div className="flex justify-between text-metallic-gold" data-testid="checkout-rewards-line">
                <span>Rewards applied</span><span>−{format(rewardsApplied)}</span>
              </div>
            )}
            <div className="w-full h-px bg-gray-200 my-1" />
            <div className="flex justify-between text-lg font-medium"><span>Total</span><span data-testid="checkout-total">{format(total)}</span></div>
            {isConverted && (
              <p className="text-xs text-gray-400 -mt-1" data-testid="checkout-currency-note">
                Prices shown in {currency}. Your card is charged in GBP ({formatGBP(total)}).
              </p>
            )}
            {customer && rewardsToEarn > 0 && (
              <p className="text-xs text-metallic-gold flex items-center gap-1" data-testid="checkout-rewards-earn">
                You'll earn {format(rewardsToEarn)} in rewards on this order.
              </p>
            )}
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="place-order-btn"
            className="mt-6 flex items-center justify-center gap-2 w-full text-white py-4 rounded-full text-sm font-medium shadow-sm bg-[#1d4ed8] hover:bg-[#1e40af] hover:shadow-md transition-all disabled:opacity-60"
          >
            {loading ? "Redirecting to payment..." : "Proceed to payment"}
          </button>
        </div>
      </form>
    </div>
  );
}
